import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewEncapsulation,
} from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { AccountAppValue, Reward, RewardStateFilterType } from "@fabric-types/app-account";
import { Store } from "@ngrx/store";
import { NavbarOptions } from "@obg/mat.ui.core";
import {
	CommonState,
	CustomerOfferAction,
	CustomerOfferSelector,
	CustomerVerificationConfigService,
	Offer,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AuthSelector,
	CORE_ROUTE,
	CustomerManagementSelector,
	ProductType,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { DocumentRef, TelemetryService } from "@obg/ngx.global.evo";
import { Route, RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { isEmpty } from "lodash-es";
import { Observable, Subscription, combineLatest, merge, of } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { DepositBonusConfigService } from "../../deposit-bonuses/deposit-bonus.config";
import { DepositBonusAction } from "../../telemetry/ui-interaction-telemetry.model";

import { WelcomeBonusClickEvent } from "./welcome-bonus.const";
import { WelcomeBonusPageParams } from "./welcome-bonus.model";

const WELCOME_BONUS_DIALOG_ID = "WELCOME_BONUS_DIALOG";

@Component({
	selector: "obg-m-welcome-bonus-container",
	templateUrl: "./welcome-bonus.container.html",
	styleUrls: ["./welcome-bonus.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class WelcomeBonusContainer implements OnInit, OnDestroy, OnChanges {
	@HostBinding("class.obg-m-welcome-bonus") hostClass = true;

	@HostBinding("class.obg-m-welcome-bonus-alt") get hostClassAlternative() {
		return this.depositBonusDetailedViewEnabled;
	}

	@Input() moduleData: { redirectTo: string } = { redirectTo: CORE_ROUTE.payment.deposit };

	items$: Observable<(Offer | Reward)[]> | undefined;
	rewards$: Observable<Reward[]> | undefined;
	offers$: Observable<Offer[]> | undefined;
	accountCreated$: Observable<boolean | undefined> | undefined;
	productParam: { product?: ProductType } | undefined;
	isClaimAllowed = true;
	hasOffers = false;
	depositBonusDetailedViewEnabled = this.depositBonusConfigService.isDepositBonusesDetailedViewEnabled();
	navbarOptions: NavbarOptions = {
		hideBackButton: true,
		preventDefaultBack: true,
		showCloseButton: true,
	};

	get route(): string {
		return this.moduleData?.redirectTo ?? CORE_ROUTE.payment.deposit;
	}

	private data$$: Subscription | undefined;
	private rewardType = RewardStateFilterType.Claimable;
	private referer: Route | undefined;
	private rewardNavigationAction$$: Subscription | undefined;

	constructor(
		private store: Store<CommonState>,
		private alertService: AbstractTranslatableAlertService,
		private customerOfferSelector: CustomerOfferSelector,
		private customerOfferAction: CustomerOfferAction,
		private changeDetector: ChangeDetectorRef,
		private customerVerificationConfigService: CustomerVerificationConfigService,
		private depositBonusConfigService: DepositBonusConfigService,
		private telemetryService: TelemetryService,
		private dialog: MatDialog,
		private documentRef: DocumentRef
	) {}

	ngOnInit() {
		this.telemetryService.track({
			event: DepositBonusAction.BonusSelectionOpen,
		});

		this.store.dispatch(this.customerOfferAction.get());

		const product$ = this.store.select<WelcomeBonusPageParams | undefined>(RouteSelector.getQueryParams).pipe(
			filter((params) => !!params),
			tap((params) => (this.productParam = params?.product ? { product: params.product } : {}))
		);

		this.rewards$ = new Observable(
			this.documentRef.obgEventBus.retrieve(AccountAppValue.GetDepositRewards, {
				rewardFilter: this.rewardType,
			})
		);

		this.offers$ = this.store.select(this.customerOfferSelector.getOffers());

		this.items$ = combineLatest([this.rewards$, this.offers$]).pipe(
			map(([rewards, offers]) => [...rewards, ...offers])
		);

		const hasDepositEntries$ = combineLatest([
			this.store.select(this.customerOfferSelector.isBusy()),
			this.rewards$,
		]).pipe(
			filter(([offersBusy]) => !offersBusy),
			switchMap(([, depositRewards]) =>
				this.store.select(this.customerOfferSelector.hasOffers()).pipe(
					tap((customerOffers) => {
						this.hasOffers = !isEmpty(depositRewards) || customerOffers;
						if (!this.hasOffers) {
							this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
						}
					})
				)
			)
		);

		const allowedAction$ = this.store.select(CustomerManagementSelector.getAllowedAction).pipe(
			ignoreNil(),
			take(1),
			tap((actions) => (this.isClaimAllowed = actions!.claimBonus.isAllowed))
		);

		const routeReferer$ = this.store.select(RouteSelector.getReferer).pipe(
			ignoreNil(),
			tap((referer) => {
				if (referer?.queryParams?.returnUrl) {
					this.referer = referer.queryParams.returnUrl;
				}
			})
		);

		this.accountCreated$ = this.customerVerificationConfigService.isNoWelcomePageAfterLoginEnabled()
			? this.store.select(AuthSelector.isFirstLogin)
			: of(false);

		this.data$$ = merge(product$, hasDepositEntries$, allowedAction$, routeReferer$).subscribe(() =>
			this.changeDetector.markForCheck()
		);
	}

	ngOnChanges({ moduleData }: SimpleChanges) {
		if (moduleData?.currentValue && moduleData.currentValue.data) {
			this.moduleData = moduleData.currentValue.data;
			this.changeDetector.detectChanges();
		}
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.rewardNavigationAction$$?.unsubscribe();
	}

	onBonusSelected(item: Reward | Offer | undefined) {
		if (!item) {
			this.trackEvent({ ItemClicked: WelcomeBonusClickEvent.DepositWithoutBonus });
			this.navigateToDeposit();
		} else if ("identifier" in item) {
			this.trackEvent({
				ItemClicked: WelcomeBonusClickEvent.ClaimBonus,
				BonusTitle: item.title,
				BonusType: item.rewardCategory,
			});
			this.onRewardAction(item);
		} else {
			this.trackEvent({
				ItemClicked: WelcomeBonusClickEvent.ClaimBonus,
				BonusTitle: item.title,
				BonusType: "Reward Offer",
			});
			this.onOfferAction(item);
		}
	}

	navigateToDeposit() {
		const redirectFromDeposit = this.referer ? { returnUrl: this.referer } : { redirect: CORE_ROUTE.home };
		const queryParams = {
			skipWelcomeBonus: true,
			skipRewardClaim: true,
			...redirectFromDeposit,
			...this.productParam,
		};
		this.store.dispatch(
			RouteAction.navigate(this.route, undefined, { queryParams, replaceUrl: true, queryParamsHandling: "merge" })
		);
	}

	onNavbarCloseClick() {
		this.trackEvent({ ItemClicked: WelcomeBonusClickEvent.Close });

		if (this.route !== CORE_ROUTE.payment.deposit) {
			this.dialog.closeAll();
			return;
		}

		this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
	}

	private onRewardAction(reward: Reward) {
		if (!this.isClaimAllowed) {
			this.alertService.open(
				{
					id: WELCOME_BONUS_DIALOG_ID,
					title: "bonus.claim-not-allowed-title",
					message: "bonus.claim-not-allowed-description",
					buttons: [
						{
							text: "general.ok",
						},
					],
				},
				[TranslationGroup.Common]
			);
		} else {
			const isOfferStateEnabled$ = this.store.select(this.customerOfferSelector.isEnabled());
			const customerOfferParams$ = isOfferStateEnabled$.pipe(
				map((isOfferStateEnabled) =>
					isOfferStateEnabled ? { skipWelcomeBonus: true } : { redirect: CORE_ROUTE.home }
				)
			);
			const replaceUrl$ = isOfferStateEnabled$.pipe(
				map((isOfferStateEnabled) => isOfferStateEnabled || undefined)
			);

			this.rewardNavigationAction$$?.unsubscribe();
			this.rewardNavigationAction$$ = combineLatest([customerOfferParams$, replaceUrl$])
				.pipe(
					tap(([customerOfferParams, replaceUrl]) =>
						this.store.dispatch(
							RouteAction.navigate(this.route, undefined, {
								queryParams: {
									...customerOfferParams,
									...this.productParam,
									rewardIdentifier: reward.identifier,
									rewardLockupType: reward.objectType,
									redirect: CORE_ROUTE.reward.active,
								},
								replaceUrl,
								queryParamsHandling: "merge",
							})
						)
					)
				)
				.subscribe();
		}
	}

	private onOfferAction(offer: Offer) {
		const queryParams = {
			skipWelcomeBonus: true,
			siteTrackingId: offer.fulfillmentId,
			...this.productParam,
		};

		this.store.dispatch(
			RouteAction.navigate(this.route, undefined, { queryParams, replaceUrl: true, queryParamsHandling: "merge" })
		);
	}

	private trackEvent(data: Record<string, string | undefined>) {
		this.telemetryService.track({
			event: DepositBonusAction.BonusSelectionClick,
			...data,
		});
	}
}
